/*
 * "Refer a client": a side sheet with the lead's name, email, and optionally
 * phone and a note. POST /partner/referrals. A 409 (already with Wealthoak,
 * or referred by someone else) and a 403 (account not active yet) come back
 * as sentences and are shown inline, as-is.
 */

import { useState } from "react";
import { ApiError, errorMessage, referrals } from "../api/client";
import { useMe } from "../auth/AuthContext";
import { Dialog } from "./bits";
import { Info } from "./icons";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NOTES_MAX = 1000;

export default function ReferSheet({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: (leadName: string) => void;
}) {
  const me = useMe();
  const pending = me.status !== "active";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameError = !name.trim() ? "Enter their name." : null;
  const emailError = !email.trim() ? "Enter their email." : !EMAIL.test(email.trim()) ? "That doesn't look like an email address." : null;
  const phoneError = phone.trim() && !/^[+\d][\d\s-]{6,19}$/.test(phone.trim()) ? "Use digits, spaces or dashes, e.g. +91 98200 12345." : null;
  const invalid = Boolean(nameError || emailError || phoneError);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid || pending) return;
    setBusy(true);
    setError(null);
    try {
      const created = await referrals.create({
        lead_name: name.trim(),
        lead_email: email.trim(),
        ...(phone.trim() ? { lead_phone: phone.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onSubmitted(created.lead_name);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      variant="sheet"
      title="Refer a client"
      description="Tell us who to talk to. Wealthoak's team contacts them; once we open their account, they appear under Clients and you earn on their subscription."
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      {pending && (
        <div className="notice is-warn" role="status">
          <Info size={16} />
          <span>Your partner account is awaiting approval. You can submit referrals once Wealthoak approves it.</span>
        </div>
      )}
      <form onSubmit={submit} noValidate>
        <div className="form-grid">
          <div className="field is-wide">
            <label className="field__label" htmlFor="ref-name">Full name</label>
            <input
              id="ref-name"
              className="input"
              autoComplete="off"
              value={name}
              maxLength={200}
              aria-invalid={touched && Boolean(nameError)}
              aria-describedby={touched && nameError ? "ref-name-err" : undefined}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ravi Kumar"
              data-autofocus
            />
            {touched && nameError && <p id="ref-name-err" className="field__error">{nameError}</p>}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="ref-email">Email</label>
            <input
              id="ref-email"
              className="input"
              type="email"
              autoComplete="off"
              value={email}
              maxLength={254}
              aria-invalid={touched && Boolean(emailError)}
              aria-describedby={touched && emailError ? "ref-email-err" : undefined}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ravi@example.com"
            />
            {touched && emailError && <p id="ref-email-err" className="field__error">{emailError}</p>}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="ref-phone">
              Phone <em>(optional)</em>
            </label>
            <input
              id="ref-phone"
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              value={phone}
              maxLength={20}
              aria-invalid={touched && Boolean(phoneError)}
              aria-describedby={touched && phoneError ? "ref-phone-err" : undefined}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98200 12345"
            />
            {touched && phoneError && <p id="ref-phone-err" className="field__error">{phoneError}</p>}
          </div>
          <div className="field is-wide">
            <label className="field__label" htmlFor="ref-notes">
              Note for the Wealthoak team <em>(optional)</em>
            </label>
            <textarea
              id="ref-notes"
              className="input"
              value={notes}
              maxLength={NOTES_MAX}
              aria-describedby="ref-notes-hint"
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Best time to call, what they're looking for…"
            />
            <p id="ref-notes-hint" className="field__hint">
              {notes.length > NOTES_MAX - 100 ? `${NOTES_MAX - notes.length} characters left.` : "Only Wealthoak sees this."}
            </p>
          </div>
        </div>

        {error && <div className="form-error" role="alert">{error}</div>}

        <div className="form-actions">
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy || pending}>
            {busy ? "Submitting…" : "Submit referral"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
